import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  useShortTermForecast,
  useHistoricalForecast,
  useAilDemand,
  useRenewableForecast,
  useMEConfig,
  usePowerplantStatus,
  usePowerplantTotalOutput,
  useRevenue,
} from '../api/GetData';
import '../components/sidebar.css';
import './MasterPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function toChartNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toChartDate(value) {
  if (!value) return null;
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toMeteredVolumeValue(row) {
  const v = Number.parseFloat(row?.metered_volume ?? row?.TNG ?? row?.tng ?? 0);
  return Number.isFinite(v) ? v : 0;
}

function toPoolPriceValue(row) {
  const v = Number.parseFloat(row?.poolprice ?? row?.pool_price ?? row?.poolPrice ?? 0);
  return Number.isFinite(v) ? v : 0;
}

function getDateBounds(rows) {
  const dates = (Array.isArray(rows) ? rows : [])
    .map(row => toChartDate(row?.datetime))
    .filter(Boolean);
  return {
    min: dates.length > 0 ? dates[0] : new Date(0),
    max: dates.length > 0 ? dates[dates.length - 1] : new Date(0),
  };
}

const PLANT_COLORS = ['#4681E1', '#de3c64', '#5dd0ff', '#f5a623', '#7ed321', '#9b59b6', '#e67e22', '#1abc9c'];

const dateFormatter = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} HE ${String(date.getHours()).padStart(2, '0')}`;

const axisLabelStyle = { fontWeight: 'Bold', fontSize: '12px', fontFamily: 'Tahoma', letterSpacing: '0.08em', fill: '#b7c8e4' };
const legendStyle   = { fontSize: '12px', fontFamily: 'Tahoma', letterSpacing: '0.08em', fill: '#e7edf7' };

// ─── Per-plant data collector ─────────────────────────────────────────────────
const PlantDataCollector = ({ plant, onData }) => {
  const { data: config }  = useMEConfig(plant);
  const assetId           = config?.asset_identifier ?? '';
  const { data: status }  = usePowerplantStatus(assetId);
  const { data: output }  = usePowerplantTotalOutput(plant);
  const { data: revenue } = useRevenue(plant);

  useEffect(() => {
    onData(plant, { config, status, output, revenue });
  }, [plant, config, status, output, revenue, onData]);

  return null;
};

// ─── Small reusable summary card ─────────────────────────────────────────────
const SummaryCard = ({ title, children }) => (
  <div className="summary-card">
    <div className="summary-card-header">{title}</div>
    <div className="summary-card-body">{children}</div>
  </div>
);

// ─── Master Overview page ─────────────────────────────────────────────────────
const MasterPage = ({ allowedPlants = [] }) => {
  const { data: stfdata }        = useShortTermForecast();
  const { data: historicalData } = useHistoricalForecast();
  const { data: ailDemandData }  = useAilDemand();
  const { data: renewableData }  = useRenewableForecast();

  const [plantDataMap, setPlantDataMap] = useState({});
  const handlePlantData = useCallback((plant, data) => {
    setPlantDataMap(prev => ({ ...prev, [plant]: data }));
  }, []);

  const plants = useMemo(() =>
    Array.isArray(allowedPlants) ? allowedPlants : (allowedPlants ? [allowedPlants] : [])
  , [allowedPlants]);

  // ── Actual Power Price ────────────────────────────────────────────────────
  const actualPriceInfo = useMemo(() => {
    if (!Array.isArray(historicalData) || !historicalData.length) return null;
    const sorted  = [...historicalData].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    const latest  = sorted[sorted.length - 1];
    if (!latest) return null;
    const dataHE   = new Date(latest.datetime).getHours();
    const currentHE = new Date().getHours() + 1;
    const offset    = currentHE - dataHE;
    const tLabel    = offset === 0 ? 't' : `t-${offset}`;
    const price     = latest.pool_price ?? latest.poolprice;
    return { he: dataHE, tLabel, price: Number(price ?? 0).toFixed(0) };
  }, [historicalData]);

  // ── Renewables T / T+1 ───────────────────────────────────────────────────
  const renewablesInfo = useMemo(() => {
    if (!renewableData) return null;
    const times  = renewableData.time  || [];
    const solar  = renewableData.solar || [];
    const wind   = renewableData.wind  || [];
    if (!times.length) return null;
    const combined   = times.map((t, i) => ({ time: t, solar: parseFloat(solar[i] || 0), wind: parseFloat(wind[i] || 0) }));
    const currentHour = new Date().getHours();
    const t  = combined.find(item => new Date(item.time).getHours() === currentHour);
    const t1 = combined.find(item => new Date(item.time).getHours() === (currentHour + 1) % 24);
    return { t, t1 };
  }, [renewableData]);

  // ── Fleet aggregates ─────────────────────────────────────────────────────
  const fleetStats = useMemo(() => {
    const entries = Object.values(plantDataMap);
    if (!entries.length) return { totalMW: '0.00', onlineCount: 0, totalCount: 0, currentRevenue: 0, previousRevenue: 0 };
    const now = new Date();
    let totalKW = 0, onlineCount = 0, currentRevenue = 0, previousRevenue = 0;
    entries.forEach(({ output, status, revenue, config }) => {
      if (output?.total_output != null) totalKW += Number(output.total_output);
      if (status === true) onlineCount++;
      const mc = Number(config?.marginal_cost ?? 0);
      if (Array.isArray(revenue)) {
        revenue.forEach(row => {
          const dt  = toChartDate(row?.datetime);
          if (!dt) return;
          const off = (dt.getFullYear() - now.getFullYear()) * 12 + (dt.getMonth() - now.getMonth());
          const val = Math.max(toMeteredVolumeValue(row) * Math.max(toPoolPriceValue(row), mc), 0);
          if (off === 0)  currentRevenue  += val;
          else if (off === -1) previousRevenue += val;
        });
      }
    });
    return { totalMW: (totalKW / 1000).toFixed(2), onlineCount, totalCount: entries.length,
      currentRevenue: Math.floor(currentRevenue), previousRevenue: Math.floor(previousRevenue) };
  }, [plantDataMap]);

  // ── STF series ───────────────────────────────────────────────────────────
  const stfBounds = useMemo(() => getDateBounds(stfdata), [stfdata]);
  const stfXData  = useMemo(() =>
    Array.isArray(stfdata) ? stfdata.map(row => toChartDate(row?.datetime)).filter(Boolean) : []
  , [stfdata]);
  const stfSeries = useMemo(() => {
    if (!Array.isArray(stfdata) || !stfdata.length) return [];
    const series = [
      { data: stfdata.map(row => toChartNumber(row?.pool_price)),          label: 'Pool Price ($)',    color: '#de3c64', showMark: false },
      { data: stfdata.map(row => toChartNumber(row?.aeso_forecast_price)), label: 'AESO Forecast ($)', color: '#545454', showMark: false },
    ];
    if (stfdata.some(row => toChartNumber(row?.rf_24h_specialists_forecast_price) !== null)) {
      series.push({ data: stfdata.map(row => toChartNumber(row?.rf_24h_specialists_forecast_price)), label: 'WiSDM Forecast ($)', color: '#2196f3', showMark: false });
    }
    return series;
  }, [stfdata]);
  const stfMax = useMemo(() => {
    if (!stfSeries.length) return undefined;
    const allVals = stfSeries.flatMap(s => s.data).filter(v => v !== null);
    return allVals.length ? Math.ceil(Math.max(...allVals) * 1.1) : undefined;
  }, [stfSeries]);

  // ── Stacked Metered Volume ────────────────────────────────────────────────
  const { mvTimeline, mvSeries, mvMax } = useMemo(() => {
    const activePlants = plants.filter(p => Array.isArray(plantDataMap[p]?.revenue));
    if (!activePlants.length) return { mvTimeline: [], mvSeries: [], mvMax: 1 };
    const now = new Date();
    const byTime = {};
    activePlants.forEach(plant => {
      (plantDataMap[plant].revenue || []).forEach(row => {
        const dt  = toChartDate(row?.datetime);
        if (!dt) return;
        const off = (dt.getFullYear() - now.getFullYear()) * 12 + (dt.getMonth() - now.getMonth());
        if (off !== 0) return;
        const key = dt.getTime();
        if (!byTime[key]) byTime[key] = { dt };
        byTime[key][plant] = toMeteredVolumeValue(row);
      });
    });
    const timeline      = Object.values(byTime).sort((a, b) => a.dt - b.dt);
    const timelineDates = timeline.map(r => r.dt);
    const series        = activePlants.map((plant, i) => ({
      data: timeline.map(r => r[plant] ?? null),
      label: (plantDataMap[plant]?.config?.asset_name || plant).toUpperCase(),
      color: PLANT_COLORS[i % PLANT_COLORS.length],
      stack: 'total', area: true, showMark: false,
    }));
    const totals = timeline.map((_, i) => series.reduce((sum, s) => sum + (s.data[i] ?? 0), 0));
    return { mvTimeline: timelineDates, mvSeries: series, mvMax: Math.ceil(Math.max(1, ...totals) * 1.1) };
  }, [plants, plantDataMap]);

  // ── Stacked Revenue ───────────────────────────────────────────────────────
  const { revTimeline, revSeries, revMax } = useMemo(() => {
    const activePlants = plants.filter(p => Array.isArray(plantDataMap[p]?.revenue));
    if (!activePlants.length) return { revTimeline: [], revSeries: [], revMax: 1 };
    const now = new Date();
    const byTime = {};
    activePlants.forEach(plant => {
      const mc = Number(plantDataMap[plant]?.config?.marginal_cost ?? 0);
      (plantDataMap[plant].revenue || []).forEach(row => {
        const dt  = toChartDate(row?.datetime);
        if (!dt) return;
        const off = (dt.getFullYear() - now.getFullYear()) * 12 + (dt.getMonth() - now.getMonth());
        if (off !== 0) return;
        const key = dt.getTime();
        if (!byTime[key]) byTime[key] = { dt };
        byTime[key][plant] = Math.max(toMeteredVolumeValue(row) * Math.max(toPoolPriceValue(row), mc), 0);
      });
    });
    const timeline      = Object.values(byTime).sort((a, b) => a.dt - b.dt);
    const timelineDates = timeline.map(r => r.dt);
    const series        = activePlants.map((plant, i) => ({
      data: timeline.map(r => r[plant] ?? null),
      label: (plantDataMap[plant]?.config?.asset_name || plant).toUpperCase(),
      color: PLANT_COLORS[i % PLANT_COLORS.length],
      stack: 'total', area: true, showMark: false,
    }));
    const totals = timeline.map((_, i) => series.reduce((sum, s) => sum + (s.data[i] ?? 0), 0));
    return { revTimeline: timelineDates, revSeries: series, revMax: Math.ceil(Math.max(1, ...totals) * 1.1) };
  }, [plants, plantDataMap]);

  const fmtCAD   = (n) => '$' + n.toLocaleString('en-CA');
  const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
  const curMonth = monthFmt.format(new Date());
  const prevMonth = monthFmt.format(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));

  return (
    <div className="master-page">
      <div className="master-inner">

        {/* Invisible per-plant data collectors */}
        {plants.map(p => <PlantDataCollector key={p} plant={p} onData={handlePlantData} />)}

        <h1 className="master-page-title">Master Overview</h1>

        {/* ── Section 1: Short Term Forecast ──────────────────────────────── */}
        <div className="master-section">
          <div className="master-section-heading"><span>Short Term Forecast</span></div>
          <div className="master-chart-card">
            {stfSeries.length > 0 ? (
              <LineChart
                height={300}
                margin={{ left: 72, top: 40, bottom: 48 }}
                slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' }, padding: 0, labelStyle: legendStyle, itemMarkWidth: 10, itemMarkHeight: 10 } }}
                xAxis={[{ id: 'date', label: 'Datetime (YYYY-MM-DD HE)', scaleType: 'time', labelStyle: axisLabelStyle, data: stfXData, min: stfBounds.min, max: stfBounds.max, valueFormatter: dateFormatter }]}
                yAxis={[{ label: 'Pool Price (CAD $)', labelStyle: axisLabelStyle, min: 0, max: stfMax }]}
                series={stfSeries}
              />
            ) : (
              <div className="chart-loading">Loading forecast data…</div>
            )}
          </div>
        </div>

        {/* ── Section 2: Fleet Status ──────────────────────────────────────── */}
        <div className="master-section">
          <div className="master-section-heading"><span>Fleet Status</span></div>
          <div className="fleet-layout">

            {/* Plant traffic light cards */}
            <div className="plant-cards-grid">
              {plants.map(plant => {
                const d    = plantDataMap[plant];
                const name = d?.config?.asset_name || plant;
                const isOn = d?.status === true;
                const kw   = d?.output?.total_output;
                const mw   = kw != null ? (Number(kw) / 1000).toFixed(2) : '—';
                return (
                  <div key={plant} className={`plant-card ${isOn ? 'online' : 'offline'}`}>
                    <div className="plant-card-name">{name}</div>
                    <div className="status-center">
                      <div className={`status-dot large ${isOn ? 'on' : 'off'}`} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span className="plant-card-mw">{mw}</span>
                      <span className="plant-card-mw-unit"> MW</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary panel */}
            <div className="summary-panel">

              <SummaryCard title="Fleet">
                <div className="fleet-hero">
                  <span className="fleet-hero-num">{fleetStats.onlineCount}</span>
                  <span className="fleet-hero-denom">/ {fleetStats.totalCount}</span>
                </div>
                <div className="fleet-hero-label">Plants Online</div>
                <div style={{ marginTop: '4px', borderTop: '1px solid #22416b', paddingTop: '8px' }}>
                  <div className="summary-row">
                    <span className="summary-label">Total Output</span>
                    <span className="summary-value highlight">{fleetStats.totalMW} MW</span>
                  </div>
                </div>
              </SummaryCard>

              <SummaryCard title="Revenue">
                <div className="summary-row">
                  <span className="summary-label">{curMonth}</span>
                  <span className="summary-value highlight">{fmtCAD(fleetStats.currentRevenue)}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{prevMonth}</span>
                  <span className="summary-value">{fmtCAD(fleetStats.previousRevenue)}</span>
                </div>
              </SummaryCard>

              {actualPriceInfo && (
                <SummaryCard title="Actual Power Price">
                  <div className="summary-row">
                    <span className="summary-label">HE {actualPriceInfo.he} ({actualPriceInfo.tLabel})</span>
                    <span className="summary-value highlight">${actualPriceInfo.price}</span>
                  </div>
                </SummaryCard>
              )}

              {ailDemandData?.datetime && (
                <SummaryCard title="AIL Demand">
                  <div className="summary-row">
                    <span className="summary-label">HE {new Date(ailDemandData.datetime).getHours() + 1}</span>
                    <span className="summary-value">{ailDemandData.aildemand ?? '—'} MW</span>
                  </div>
                </SummaryCard>
              )}

              {renewablesInfo && (
                <SummaryCard title="Renewables">
                  {renewablesInfo.t && (
                    <div className="summary-row">
                      <span className="summary-label">HE {new Date(renewablesInfo.t.time).getHours() + 1} (t)</span>
                      <span className="summary-value">
                        ☀ {Number(renewablesInfo.t.solar).toFixed(0)}  💨 {Number(renewablesInfo.t.wind).toFixed(0)} MW
                      </span>
                    </div>
                  )}
                  {renewablesInfo.t1 && (
                    <div className="summary-row">
                      <span className="summary-label">HE {new Date(renewablesInfo.t1.time).getHours() + 1} (t+1)</span>
                      <span className="summary-value">
                        ☀ {Number(renewablesInfo.t1.solar).toFixed(0)}  💨 {Number(renewablesInfo.t1.wind).toFixed(0)} MW
                      </span>
                    </div>
                  )}
                </SummaryCard>
              )}

            </div>
          </div>
        </div>

        {/* ── Sections 3 & 4: side-by-side stacked charts ─────────────────── */}
        <div className="charts-row">

          <div className="master-section" style={{ marginBottom: 0 }}>
            <div className="master-section-heading"><span>Metered Volume — {curMonth}</span></div>
            <div className="master-chart-card">
              {mvSeries.length > 0 ? (
                <LineChart
                  height={280}
                  margin={{ left: 72, top: 40, bottom: 48 }}
                  slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' }, padding: 0, labelStyle: legendStyle, itemMarkWidth: 10, itemMarkHeight: 10 } }}
                  xAxis={[{ scaleType: 'time', data: mvTimeline, label: 'Datetime', labelStyle: axisLabelStyle, valueFormatter: dateFormatter }]}
                  yAxis={[{ label: 'MWH', labelStyle: axisLabelStyle, min: 0, max: mvMax }]}
                  series={mvSeries}
                />
              ) : (
                <div className="chart-loading">Loading metered volume data…</div>
              )}
            </div>
          </div>

          <div className="master-section" style={{ marginBottom: 0 }}>
            <div className="master-section-heading"><span>Revenue — {curMonth}</span></div>
            <div className="master-chart-card">
              {revSeries.length > 0 ? (
                <LineChart
                  height={280}
                  margin={{ left: 80, top: 40, bottom: 48 }}
                  slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' }, padding: 0, labelStyle: legendStyle, itemMarkWidth: 10, itemMarkHeight: 10 } }}
                  xAxis={[{ scaleType: 'time', data: revTimeline, label: 'Datetime', labelStyle: axisLabelStyle, valueFormatter: dateFormatter }]}
                  yAxis={[{ label: 'CAD $', labelStyle: axisLabelStyle, min: 0, max: revMax }]}
                  series={revSeries}
                />
              ) : (
                <div className="chart-loading">Loading revenue data…</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MasterPage;
